import React, { useState, ReactElement } from "react";
import { Link } from "react-router-dom";

import urlPatterns from "../../../urls";
import { MemberShoppingCart } from "../../../types/__generated__/MemberShoppingCart";
import { Credit } from "../../../types/__generated__/Credit";
import { extractNumberFromString, getWineBaseColor, wait } from "../../../helpers/tools";
import { CONTAINER_MAX_WIDTH } from "../../../styles/variables";
import {
  WINE_LIST_SLUGS,
  MIN_WINE_TITLE_CHARACTER_COUNT_TO_HYPHENATE,
  MIN_WINE_TITLE_NAME_CHARACTER,
} from "../../../helpers/constants";
import BookmarkIcon from "../../atoms/BookmarkIcon";
import { EllipsisTag as Tag, EllipsisTagText as TagLabel } from "../../atoms/Tag";

import galleryImage from "../../../assets/images/wine/default-wine-cover.jpg";
import { isLoggedIn } from "../../../helpers/auth";
import {
  trackingEvents,
  trackingEventClickedTagOnProductCardOptions,
  useTrackEvent,
} from "../../../helpers/Analytics";

import WineCardAddToBoxOrCartButton from "../../atoms/WineCardAddToBoxOrCartButton";
import ButtonWithCheckMark from "../../atoms/ButtonWithCheckMark";
import { getCashAmountLabel } from "../../../helpers/i18n";
import {
  WineCardContainer,
  WineCardPreviewImg,
  WineCardFooter,
  WineCardPanelLeft,
  WineCardPanelRight,
  WineCardPrice,
  TitleAndBookmarkContainer,
  MemberInfoLabel,
  WineCardPanelBottom,
  Tags,
  WineTitleText,
  WineImageHolder,
  LowStockInfoLabel,
  BookmarkContainer,
  MemberInfoWrapper,
  WineRatingInfo,
} from "./style";

interface TagType {
  tagName: string;
  tagValue: string;
}

interface WineCardProps {
  memberShoppingCartQuery: MemberShoppingCart | null | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  wine: any; // TODO: How to defined a consistent type of wine from multiple queries
  image: string;
  title: string;
  name: string;
  wineClassId: number;
  wineTypeId: number;
  wineClassName: string;
  wineMakerName: string;
  region: string;
  regionState: string;
  country: string;
  slug: string;
  price: number;
  memberLikelihood?: string;
  memberWineRatingScore?: number;
  isInWineCollections?: boolean;
  toggleAddBottleTray?: Function | null | undefined;
  toggleBookmarksTray?: Function;
  setHeaderDisplay?: Function;
  isLowStock?: boolean;
  isBookmarked?: boolean;
  toggleWineDetailsTray: Function;
  onTagClicked?: Function;
  addBottleToCredit?: Credit;
}

const WineCard = ({
  image,
  isBookmarked = false,
  isInWineCollections = false,
  isLowStock = false,
  memberLikelihood,
  memberShoppingCartQuery,
  memberWineRatingScore,
  price,
  setHeaderDisplay = () => {},
  slug,
  title,
  toggleWineDetailsTray,
  toggleAddBottleTray = () => {},
  toggleBookmarksTray = () => {},
  wine,
  wineClassId,
  wineTypeId,
  onTagClicked,
  addBottleToCredit,
  ...props
}: WineCardProps) => {
  const [quantity, _setQuantity] = useState<number>(1);
  const [isCheckButtonShown, setIsCheckButtonShown] = useState<boolean>(false);

  const trackEvent = useTrackEvent();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleWineCardClick = (event: any) => {
    if (!event.ctrlKey && !event.metaKey) {
      event.preventDefault();
      toggleWineDetailsTray(slug);
    }
  };

  const renderLowStock = (): ReactElement => (
    <MemberInfoWrapper>
      <LowStockInfoLabel isInWineCollections={isInWineCollections}>Low Stock</LowStockInfoLabel>
    </MemberInfoWrapper>
  );

  const renderMemberRating = (): ReactElement => (
    <MemberInfoWrapper>
      <MemberInfoLabel isInWineCollections={isInWineCollections}>Your rating</MemberInfoLabel>
      <WineRatingInfo rating={memberWineRatingScore} isInAllWines={!isInWineCollections} />
    </MemberInfoWrapper>
  );

  const renderMemberLikelihood = (): ReactElement => (
    <MemberInfoWrapper>
      <MemberInfoLabel isInWineCollections={isInWineCollections}>
        {`${memberLikelihood}% match`}
      </MemberInfoLabel>
    </MemberInfoWrapper>
  );

  const renderMemberInfo = (): ReactElement => {
    if (isLowStock) {
      return renderLowStock();
    }
    if (memberWineRatingScore) {
      return renderMemberRating();
    }
    if (memberLikelihood) {
      return renderMemberLikelihood();
    }

    return <MemberInfoWrapper />;
  };

  /**
   * Gets the tags for Collections and All Wines cards.
   *
   * @returns {Array}
   * */
  const getTagsArray = (): Array<TagType> => {
    const { name, wineClassName, region, regionState, country, wineMakerName } = props;

    // Prevents duplication of tags with same name and class (Eg. Rose).
    const displayWineClassName = wineClassName && wineClassName !== name;

    const tags = [];
    if (name) {
      tags.push({ tagName: "wineTypeName", tagValue: name });
    }
    if (displayWineClassName) {
      tags.push({ tagName: "wineClassName", tagValue: wineClassName });
    }
    if (region) {
      tags.push({ tagName: "wineRegionName", tagValue: region });
    }
    if (regionState) {
      tags.push({ tagName: "wineRegionState", tagValue: regionState });
    }
    if (country) {
      tags.push({ tagName: "wineCountryName", tagValue: country });
    }
    if (wineMakerName) {
      tags.push({ tagName: "wineMakerName", tagValue: wineMakerName });
    }

    return tags;
  };

  /**
   * Formats the bottle title name to force ellipsis for long texts in xlUp screens in 1 line.
   *
   * @param {String}
   * @returns {String}
   * */
  const formatTitle = (originalTitle = ""): string => {
    const windowWidth = extractNumberFromString(`${window.innerWidth}`);
    const isLgUpContainer = windowWidth > CONTAINER_MAX_WIDTH.LG_UP;
    if (isLgUpContainer && originalTitle.length > MIN_WINE_TITLE_NAME_CHARACTER) {
      // Force replace the last three characters with ellipsis.
      // When, <Container> width is more than 1290px,
      // ...then wine card widths remains consistent at 299px for all screen sizes. higher than 1290px.
      return `${originalTitle.slice(0, MIN_WINE_TITLE_NAME_CHARACTER - 3)}...`;
    }
    return originalTitle;
  };

  /**
   * Toggles the state of isCheckButtonShown to show <ButtonWithCheckMark>
   * ...for 2 seconds after adding bottle to cart or tray.
   *
   * @param {Object} response
   * */
  const onAddToBoxOrCartComplete = async (response = {}) => {
    // @ts-ignore
    const { data } = response || {};
    const { addWineToNextBox } = data || {};
    const { errors } = addWineToNextBox || {};
    if (!errors) {
      // This error check passes while adding to cart.
      // And, prevents showing the "check" button for gift subscribers,
      // who try to add more wines than allowed without adding a payment card.
      setIsCheckButtonShown(true);
      await wait(2000); // 2 seconds
      setIsCheckButtonShown(false);
    }
  };

  /**
   * Handles the tracking and scroll event when clicking on a tag.
   *
   * @param {String} tagName
   * @param {String} tagValue
   * */
  const handleClickOnTag = (tagName: string, tagValue: string) => {
    trackEvent(trackingEvents.TRACKING_EVENT_CLICKED_TAG_ON_WINE_CARD, {
      [trackingEventClickedTagOnProductCardOptions.TAG_NAME]: tagName,
      [trackingEventClickedTagOnProductCardOptions.TAG_VALUE]: tagValue,
      [trackingEventClickedTagOnProductCardOptions.IS_FROM_DETAILS]: false,
    });

    onTagClicked && onTagClicked();

    window.scrollTo(0, 0);
  };

  const colorTheme = getWineBaseColor(wineClassId, wineTypeId);

  const tags = getTagsArray();
  const bottleTitleLength = title.length;

  return (
    <WineCardContainer isInCollections={isInWineCollections}>
      {isLoggedIn() && (
        <BookmarkContainer onClick={() => toggleBookmarksTray(wine)}>
          <BookmarkIcon isBookmarked={isBookmarked} />
        </BookmarkContainer>
      )}
      <Link to={urlPatterns.WINE_DETAILS(slug)} onClick={(event) => handleWineCardClick(event)}>
        <WineImageHolder>
          <WineCardPreviewImg src={image || galleryImage} alt={title} />
        </WineImageHolder>
      </Link>
      <WineCardFooter>
        <TitleAndBookmarkContainer>
          <Link to={urlPatterns.WINE_DETAILS(slug)} onClick={handleWineCardClick}>
            <WineTitleText
              isInWineCollections={isInWineCollections}
              addHyphen={bottleTitleLength > MIN_WINE_TITLE_CHARACTER_COUNT_TO_HYPHENATE}
            >
              {formatTitle(title)}
            </WineTitleText>
          </Link>
        </TitleAndBookmarkContainer>
        <Tags color={colorTheme}>
          {tags.map(({ tagName, tagValue }) => (
            <Tag key={tagValue}>
              <Link
                to={{
                  pathname: urlPatterns.WINE_LIST(WINE_LIST_SLUGS.ALL_WINES),
                  search: `?search=${encodeURIComponent(tagValue)}`,
                }}
                onClick={() => handleClickOnTag(tagName, tagValue)}
              >
                <TagLabel>{tagValue}</TagLabel>
              </Link>
            </Tag>
          ))}
        </Tags>
        <WineCardPanelBottom>
          <WineCardPanelLeft>{renderMemberInfo()}</WineCardPanelLeft>
          <WineCardPanelRight>
            <WineCardPrice>{getCashAmountLabel(price)}</WineCardPrice>
            {isCheckButtonShown ? (
              <ButtonWithCheckMark disabled />
            ) : (
              <WineCardAddToBoxOrCartButton
                isInWineCollections={isInWineCollections}
                memberShoppingCartQuery={memberShoppingCartQuery}
                wine={{ ...wine, quantity }}
                label="ADD"
                submittingLabel="ADD"
                toggleTray={toggleAddBottleTray ? () => toggleAddBottleTray({ ...wine, quantity }) : null}
                toggleAddWineTray={() => setHeaderDisplay()}
                ghost={false}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                extraCallback={(response: any) => onAddToBoxOrCartComplete(response)}
                addBottleToCredit={addBottleToCredit}
              />
            )}
          </WineCardPanelRight>
        </WineCardPanelBottom>
      </WineCardFooter>
    </WineCardContainer>
  );
};

export default WineCard;
